import {
  AreaConfig,
  AreaConfigResponse,
  TypeCurveScenarioFormData,
  GlobalConfig,
  InventoryConfig,
  InfillAssumptions,
  TypeWellConfigRow,
  ScalingConfigRow,
  GeneralValidationRule,
  StreamThreshold,
  ValidationConfigs,
  WaterForecastSource,
} from './types';

// --- Formation Priority ---

export const FORMATION_PRIORITY_ITEMS = [
  { value: 'depth', label: 'Depth (ft)' },
  { value: 'type_curve_status', label: 'Type Curve Status' },
  { value: 'eur', label: 'EUR' },
];

export const formationPriorityValueToLabel = (value: string): string =>
  FORMATION_PRIORITY_ITEMS.find((i) => i.value === value)?.label ?? value;

export const formationPriorityLabelToValue = (label: string): string =>
  FORMATION_PRIORITY_ITEMS.find((i) => i.label === label)?.value ?? label;

// --- Section Defaults ---

export const defaultGlobalConfig: GlobalConfig = {
  name: '',
  dcaScenarioId: null,
  dcaScenarioName: null,
  status: 'eval_upside',
  notes: '',
  createdBy: '',
};

export const defaultAreaConfig: AreaConfig = {
  basinAreaCategory: null,
  areaGeometryId: null,
  areaGeometryName: null,
  structureGridCollectionId: null,
  structureGridCollectionName: null,
  basinName: null,
  subAreaCategory: null,
  operatorNames: [],
  subAreas: [],
  developmentType: 'Horizontal',
  algorithmicFormationPriority: true,
};

export const defaultInfillAssumptions: InfillAssumptions = {
  includeActiveUpsideLtds: false,
  formationLimitPercent: 100,
  developmentAreaLimitPercent: 100,
  ltdMinimum: 0,
};

export const defaultInventoryConfig: InventoryConfig = {
  formationPriority: FORMATION_PRIORITY_ITEMS.map((i) => i.label),
  eurBasis: 'single_slot',
  eurStream: 'oil',
  depthOrder: 'deep_first',
  allowOverflow: false,
  allowPartialSlots: false,
  minSlotLength: 4000,
  infillAssumptions: { ...defaultInfillAssumptions },
};

/** Default Gas Shrink Factor (%) for a Type Well Profile Config row. */
export const DEFAULT_GAS_SHRINK_PCT = 75;

/** Default NGL Yield (bbl/MMcf) for a Type Well Profile Config row. */
export const DEFAULT_NGL_YIELD = 120;

/**
 * Default ratio input for each Ratio water forecast source: a watercut (%)
 * for oil and a WGR (bbl/MMcf) for gas.
 */
export const DEFAULT_WATER_RATIO: Record<WaterForecastSource, number> = {
  oil: 50,
  gas: 40,
};

/** The values a new Type Well Profile Config row starts with. */
export const defaultTypeWellConfig: TypeWellConfigRow = {
  id: null,
  isFallback: false,
  tcFormationName: null,
  mainStream: 'oil',
  typeWellAggregation: 'p50',
  dcaScenarioId: null,
  dcaScenarioName: null,
  gasShrinkPct: DEFAULT_GAS_SHRINK_PCT,
  nglYield: DEFAULT_NGL_YIELD,
  waterForecastOption: 'dca',
  waterForecastSource: null,
  watercutPct: null,
  wgr: null,
};

/**
 * The Fallback row, which applies to wells in the basin area that fall
 * outside the mapped formations or are not landed.
 */
export const defaultFallbackTypeWellConfig: TypeWellConfigRow = {
  ...defaultTypeWellConfig,
  isFallback: true,
};

export const defaultScalingConfig: ScalingConfigRow = {
  id: null,
  scalingVariable: 'perforated_lateral_length_ft',
  scalingMethod: 'linear',
  degradationFactorPercent: 0,
  degradationBase: 0,
};

export const defaultGeneralRule: GeneralValidationRule = {
  id: null,
  overrideVerification: false,
  validationType: 'new_wells_not_landed',
  threshold: 0,
};

export const defaultStreamThreshold: StreamThreshold = {
  id: null,
  overrideVerification: false,
  validationType: 'upper_probability_threshold',
  stream: 'oil',
  thresholdPercent: 0,
  wellSetDefinition: 'analog_wells',
};

export const defaultValidationConfigs: ValidationConfigs = {
  generalRules: [
    {
      id: null,
      overrideVerification: false,
      validationType: 'time_since_last_verification',
      threshold: 6,
    },
    {
      id: null,
      overrideVerification: false,
      validationType: 'new_wells',
      threshold: 5,
    },
    {
      id: null,
      overrideVerification: true,
      validationType: 'relative_sample_size',
      threshold: 25,
    },
    {
      id: null,
      overrideVerification: false,
      validationType: 'stimulation_design_change',
      threshold: 15,
    },
  ],
  streamThresholds: [
    {
      id: null,
      overrideVerification: false,
      validationType: 'upper_probability_threshold',
      stream: 'oil',
      thresholdPercent: 0,
      wellSetDefinition: 'analog_wells',
    },
    {
      id: null,
      overrideVerification: false,
      validationType: 'lower_probability_threshold',
      stream: 'oil',
      thresholdPercent: 0,
      wellSetDefinition: 'analog_wells',
    },
    {
      id: null,
      overrideVerification: false,
      validationType: 'upper_probability_threshold',
      stream: 'gas',
      thresholdPercent: 0,
      wellSetDefinition: 'analog_wells',
    },
    {
      id: null,
      overrideVerification: false,
      validationType: 'lower_probability_threshold',
      stream: 'gas',
      thresholdPercent: 0,
      wellSetDefinition: 'analog_wells',
    },
    {
      id: null,
      overrideVerification: false,
      validationType: 'upper_probability_threshold',
      stream: 'water',
      thresholdPercent: 0,
      wellSetDefinition: 'analog_wells',
    },
    {
      id: null,
      overrideVerification: false,
      validationType: 'lower_probability_threshold',
      stream: 'water',
      thresholdPercent: 0,
      wellSetDefinition: 'analog_wells',
    },
  ],
};

// --- Combined Form Defaults ---

export const scenarioFormDefaultValues: TypeCurveScenarioFormData = {
  globalConfig: { ...defaultGlobalConfig },
  areaConfig: null,
  formationMappings: [],
  inventoryConfig: null,
  typeWellConfigs: [],
  scalingConfigs: [],
  validationConfigs: { ...defaultValidationConfigs },
};

// --- Initial Area Config (for sidebar "Add New") ---

export const INITIAL_AREA_CONFIG: AreaConfigResponse = {
  id: 'local',
  // Sentinel value — every concrete usage spreads this and overrides
  // elementId with a fresh crypto.randomUUID() (see area-panel.tsx).
  elementId: '',
  href: '',
  auth: [],
  basinName: null,
  developmentType: null,
  subAreaCategory: null,
  algorithmicFormationPriority: true,
  operatorNames: [],
  areaGeometry: null,
  structureGridCollection: null,
  subAreas: [],
  resources: {
    formationMappings: { href: '', auth: [] },
    inventoryConfigs: { href: '', auth: [] },
    typeWellConfigs: { href: '', auth: [] },
    scalingConfigs: { href: '', auth: [] },
    validationConfigs: { href: '', auth: [] },
  },
};
